import { useEffect, useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  CampoFecha,
  CampoHora,
  ChipsSeleccion,
  Icon,
  TarjetasOpcion,
  type OpcionTarjeta,
} from "@/components/ds";
import { capacitacionesIaApi } from "@/lib/api/capacitaciones-ia";
import { ApiError } from "@/lib/api";
import { cleanRut, formatRut } from "@/lib/rut";
import type {
  DestinatarioPersona,
  DestinatariosOpciones,
  EnvioCadencia,
  ModoEnvio,
  Programa,
  TematicaDestinatarioRef,
  TematicaDestinatarioTipo,
} from "@/types/capacitaciones-ia";
import { toast } from "sonner";

const CADENCIA_LABEL: Record<EnvioCadencia, string> = {
  semanal: "semana",
  quincenal: "quincena",
  mensual: "mes",
};

/**
 * Kill-switch del modo "Manual" (envío inmediato).
 *
 * El stakeholder todavía no validó el envío manual, y ofrecerlo al lado de la
 * programación automática confunde: son dos promesas distintas —"sale ahora,
 * una sola vez" contra "una temática por período"— en la misma ventana.
 *
 * Se OCULTA en la interfaz, no se borra. El backend sigue aceptando
 * `modo=manual` (`ProgramarEnviosController` sólo capea el modo automática,
 * detrás de `config('capacitaciones.envios_automaticos')`), así que volver a
 * mostrarlo es poner esta constante en `true` y nada más.
 *
 * Mientras esté en `false`:
 *   - el selector "Modo de envío" no se dibuja: con una sola opción es ruido, y
 *     además delataría que existe otro modo;
 *   - toda programación NUEVA sale en `automatica`, sin importar el
 *     `modoInicial` que pida quien abre el drawer;
 *   - lo que YA se programó en manual se sigue leyendo tal cual en la ficha de
 *     sólo lectura ("Salió de inmediato…"). Esconder la función no habilita a
 *     mentir sobre lo que ya pasó.
 *
 * El tipo es `boolean` a propósito y no el literal `false`: con el literal, TS
 * daría por imposibles las comparaciones contra `"manual"` que este archivo
 * necesita conservar para cuando la función se vuelva a prender.
 */
export const ENVIO_MANUAL_HABILITADO: boolean = false;

const MODOS: OpcionTarjeta<ModoEnvio>[] = [
  {
    valor: "automatica",
    titulo: "Automática",
    descripcion: "Una temática por período, sin intervención",
    icono: "event_repeat",
  },
  {
    valor: "manual",
    titulo: "Manual",
    descripcion: "Sale ahora, una sola vez",
    icono: "send",
  },
];

function hoySantiago(): string {
  // America/Santiago vía Intl — evita el corrimiento de `new Date().toISOString()` en UTC.
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Santiago" });
}

function formatFechaLarga(iso: string): string {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("es-CL", {
    day: "numeric",
    month: "long",
  });
}

/** Un programa se puede seguir programando mientras le quede al menos una
 * temática `pendiente`. Sin pendientes no hay nada que confirmar: el drawer
 * pasa a ser la vista de lo ya programado (`soloLectura`). */
function tienePendientes(programa: Programa): boolean {
  return programa.temas.some((t) => t.estado_negocio === "pendiente");
}

function destinatarioHeredado(
  programa: Programa
): { tipo: TematicaDestinatarioTipo; refs: TematicaDestinatarioRef[] } {
  // "Al abrirse hereda los destinatarios de los envíos anteriores del
  // programa" (spec §3.4) — se busca la temática más reciente que YA tenga
  // un destinatario definido (todas las de una misma tanda comparten tipo y
  // refs, así que la última con valor alcanza).
  const conDestino = [...programa.temas].reverse().find((t) => t.destinatario_tipo !== null);
  return {
    tipo: conDestino?.destinatario_tipo ?? "empresa",
    refs: conDestino?.destinatario_refs ?? [],
  };
}

export interface ProgramarEnviosDrawerProps {
  abierto: boolean;
  onCerrar: () => void;
  /** Programas candidatos — sólo `listo`/`activo` (spec §3.1: "Programar" sólo aparece en esos dos estados). */
  programas: Programa[];
  /** Precargado cuando se abre desde la acción "Programar" de una fila (spec §3.4). */
  programaInicialId: number | null;
  /** Modo con el que abre el drawer. Por defecto `automatica` (acción
   * "Programar" de Pantalla 2); Pantalla 3 lo abría en `manual` desde
   * "Enviar una capacitación" (spec §4). Mientras
   * `ENVIO_MANUAL_HABILITADO` sea `false`, pedir `manual` acá no tiene
   * efecto: `hidratar()` fuerza `automatica`. */
  modoInicial?: ModoEnvio;
  /** Padrón real para "Llega a N" — la carga la página una sola vez. */
  opciones: DestinatariosOpciones | null;
  /** Se llama tras confirmar con éxito: el padre refresca, cambia a Plan de
   * envíos y deja el programa visible con la fecha de referencia del envío. */
  onProgramado: (programaId: number, fechaReferencia: Date) => void;
}

/**
 * Drawer "Programar envíos" (E3, 560px, design #1399 decisión 3 + spec §3.4).
 *
 * Tiene DOS modos, decididos por el programa seleccionado, no por quien lo
 * abre: mientras le queden temáticas `pendiente` es un formulario; cuando no
 * le queda ninguna es la ficha de sólo lectura de lo que ya se programó. El
 * backend nunca re-programa una temática ya programada
 * (`programarEnvios()` sólo toca `status=planificada AND fecha_programada IS
 * NULL`, y responde 422 si no hay ninguna), así que un formulario editable en
 * ese estado prometía algo que la API iba a rechazar.
 *
 * El modo Manual está OCULTO hoy en la interfaz (`ENVIO_MANUAL_HABILITADO`),
 * pero su código se conserva entero porque el backend lo sigue soportando y la
 * función se va a prender cuando el stakeholder la valide. Todo lo que sigue
 * describe cómo se comporta cuando está prendido.
 *
 * El modo Manual en este backend dispara TODAS las temáticas pendientes del
 * programa de inmediato (`CapacitacionesIaPocService::programarEnvios()`
 * itera `tematicasPendientes` completo cuando `modo=manual`) — a diferencia
 * del prototipo, que mockeaba "Enviar ahora" como un envío de a una. Por eso
 * "Envíos generados" usa el mismo número de pendientes en ambos modos, no un
 * `1` fijo para manual.
 */
export function ProgramarEnviosDrawer({
  abierto,
  onCerrar,
  programas,
  programaInicialId,
  modoInicial = "automatica",
  opciones,
  onProgramado,
}: ProgramarEnviosDrawerProps) {
  const [programaId, setProgramaId] = useState<number | null>(null);
  const [modo, setModo] = useState<ModoEnvio>("automatica");
  const [cadencia, setCadencia] = useState<EnvioCadencia>("semanal");
  const [primerEnvio, setPrimerEnvio] = useState(hoySantiago());
  const [hora, setHora] = useState("09:00");
  const [destinatarioTipo, setDestinatarioTipo] = useState<TematicaDestinatarioTipo>("empresa");
  const [destinatarioRefs, setDestinatarioRefs] = useState<TematicaDestinatarioRef[]>([]);
  const [verExcluidos, setVerExcluidos] = useState(false);
  const [personas, setPersonas] = useState<DestinatarioPersona[]>([]);
  const [cargandoPersonas, setCargandoPersonas] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const candidatos = useMemo(
    () => programas.filter((p) => p.estado === "listo" || p.estado === "activo"),
    [programas]
  );

  /**
   * Deja el formulario reflejando `p`: los defaults de una programación nueva
   * cuando todavía quedan pendientes, o lo que se eligió la última vez cuando
   * ya no queda nada por programar.
   *
   * Sin esta segunda rama, el drawer bloqueado mostraría "hoy" y "09:00" —los
   * defaults— como si fueran la programación real. Un campo deshabilitado con
   * un valor inventado miente peor que uno editable.
   */
  function hidratar(p: Programa, modoPorDefecto: ModoEnvio) {
    const programacion = p.ultimo_envio_programado;

    if (!tienePendientes(p) && programacion !== null) {
      setModo(programacion.modo);
      setCadencia(programacion.cadencia ?? "semanal");
      setPrimerEnvio(programacion.primer_envio ?? "");
      setHora(programacion.hora_envio ?? "");
      setDestinatarioTipo(programacion.destinatario_tipo);
      setDestinatarioRefs(programacion.destinatario_refs);
      return;
    }

    const heredado = destinatarioHeredado(p);
    // Con el manual oculto, una programación nueva siempre nace `automatica`:
    // el `modoPorDefecto` que pide quien abre el drawer no puede colar un modo
    // que la interfaz ya no ofrece cambiar (ver `ENVIO_MANUAL_HABILITADO`).
    setModo(ENVIO_MANUAL_HABILITADO ? modoPorDefecto : "automatica");
    setCadencia("semanal");
    setPrimerEnvio(hoySantiago());
    setHora("09:00");
    setDestinatarioTipo(heredado.tipo);
    setDestinatarioRefs(heredado.refs);
  }

  useEffect(() => {
    if (!abierto) return;
    const pedido =
      programaInicialId !== null
        ? candidatos.find((p) => p.id === programaInicialId)
        : undefined;
    // Sin programa pedido (Pantalla 3, "Enviar una capacitación") se prefiere
    // el primero que TENGA pendientes: abrir en sólo lectura un drawer al que
    // se entró justamente para enviar sería un callejón sin salida.
    const inicial = pedido ?? candidatos.find(tienePendientes) ?? candidatos[0] ?? null;
    if (!inicial) return;
    setProgramaId(inicial.id);
    hidratar(inicial, modoInicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, programaInicialId, modoInicial]);

  useEffect(() => {
    if (destinatarioTipo !== "persona" || personas.length > 0 || cargandoPersonas) return;
    setCargandoPersonas(true);
    // Endpoint de capacitaciones, NO `/personas?all=1`: ese es el listado de
    // RRHH e incluye inactivos a propósito, así que el selector ofrecía
    // trabajadores que el envío después descartaba en silencio. El filtro de
    // elegibilidad vive en el backend (DestinatariosService) y NO se
    // reimplementa acá: dos copias de la regla derivan.
    capacitacionesIaApi
      .getDestinatariosPersonas()
      .then((res) => setPersonas(res.data))
      .catch(() => setPersonas([]))
      .finally(() => setCargandoPersonas(false));
  }, [destinatarioTipo, personas.length, cargandoPersonas]);

  const programa = candidatos.find((p) => p.id === programaId) ?? null;

  function cambiarPrograma(idStr: string) {
    const nuevo = candidatos.find((p) => String(p.id) === idStr);
    if (!nuevo) return;
    setProgramaId(nuevo.id);
    // Se re-hidrata TODO, no sólo los destinatarios: cambiar de un programa
    // sin pendientes a uno editable tiene que limpiar la fecha/hora guardadas
    // del anterior, o se enviarían como si el usuario las hubiera elegido.
    hidratar(nuevo, modo);
  }

  function elegirTipo(tipo: TematicaDestinatarioTipo) {
    setDestinatarioTipo(tipo);
    setDestinatarioRefs([]);
  }

  const pendientes = programa ? programa.temas.filter((t) => t.estado_negocio === "pendiente") : [];
  const enviadas = programa ? programa.temas.filter((t) => t.estado_negocio === "enviada").length : 0;
  const yaProgramadas = programa
    ? programa.temas.filter((t) => t.estado_negocio === "programada").length
    : 0;

  const resumenTem = programa
    ? `${programa.temas.length} temáticas en total · ${enviadas} ya enviadas · ${yaProgramadas} ya programadas · ${
        pendientes.length > 0 ? `${pendientes.length} por programar` : "ninguna por programar"
      }`
    : "";

  // Sin pendientes no hay nada que confirmar: el drawer muestra lo programado
  // y no acepta ediciones (el backend tampoco: 422 "no hay temáticas
  // pendientes"). Es una propiedad del programa elegido, así que cambiar de
  // programa en el selector devuelve el formulario si el nuevo sí tiene.
  const soloLectura = programa !== null && !tienePendientes(programa);
  const programacion = programa?.ultimo_envio_programado ?? null;
  // Un programa cuyas temáticas salieron una a una por el flujo viejo (endpoint
  // `enviar`) nunca pasó por acá y no tiene qué mostrar en cadencia/hora.
  // Antes que inventar valores, se oculta el bloque.
  const muestraCalendario = !soloLectura || programacion !== null;
  const enviosDeLaTanda = soloLectura
    ? (programacion?.tematicas_afectadas ?? yaProgramadas + enviadas)
    : pendientes.length;

  const nDestinatarios = useMemo(() => {
    if (!opciones) return 0;
    if (destinatarioTipo === "empresa") return opciones.dotacion;
    if (destinatarioTipo === "cargo") {
      return opciones.cargos
        .filter((c) => destinatarioRefs.includes(c.id))
        .reduce((acc, c) => acc + c.conteo, 0);
    }
    if (destinatarioTipo === "area") {
      return opciones.areas
        .filter((a) => destinatarioRefs.includes(a.id))
        .reduce((acc, a) => acc + a.conteo, 0);
    }
    return destinatarioRefs.length; // persona: 1 seleccionado = 1 destinatario
  }, [opciones, destinatarioTipo, destinatarioRefs]);

  const excluidos = opciones?.excluidos ?? null;
  const hayExcluidos = (excluidos?.total ?? 0) > 0;

  // "1 de 2 trabajadores" sólo cuando hay diferencia que explicar. Sin
  // excluidos, un "2 de 2" es ruido: la tarjeta vuelve a decir "2 trabajadores".
  const descripcionEmpresa = !opciones
    ? "— trabajadores"
    : hayExcluidos
      ? `${opciones.dotacion} de ${opciones.dotacion_total} trabajadores`
      : `${opciones.dotacion} ${opciones.dotacion === 1 ? "trabajador" : "trabajadores"}`;

  const tarjetasDestino: OpcionTarjeta<TematicaDestinatarioTipo>[] = [
    { valor: "empresa", titulo: "Toda la empresa", icono: "domain", descripcion: descripcionEmpresa },
    { valor: "cargo", titulo: "Por cargo", icono: "badge", descripcion: `${opciones?.cargos.length ?? 0} cargos` },
    ...(opciones && opciones.areas.length > 0
      ? ([{ valor: "area", titulo: "Por área", icono: "location_on", descripcion: `${opciones.areas.length} áreas` }] as OpcionTarjeta<TematicaDestinatarioTipo>[])
      : []),
    { valor: "persona", titulo: "Trabajador específico", icono: "person", descripcion: "Busca por nombre o RUT" },
  ];

  const resumenEnvio = soloLectura
    ? programacion === null
      ? "Este programa no tiene temáticas por programar. Sus envíos se hicieron uno a uno, sin una programación automática."
      : modo === "automatica"
        ? `Se programaron ${enviosDeLaTanda} envíos desde el ${formatFechaLarga(primerEnvio)}, una temática por ${CADENCIA_LABEL[cadencia]}. El cuestionario de cada una se genera con IA el mismo día del envío.`
        : `Salió de inmediato: ${enviosDeLaTanda} temáticas con el cuestionario generado en ese momento. Cada trabajador recibió un correo con su enlace único.`
    : modo === "automatica"
      ? `El primer envío sale el ${formatFechaLarga(primerEnvio)}, luego una temática por ${CADENCIA_LABEL[cadencia]} hasta cubrir las ${pendientes.length} temáticas que faltan de ${programa?.nombre ?? "este programa"}. El cuestionario de cada una se genera con IA el mismo día del envío.`
      : "Sale de inmediato con el cuestionario generado en ese momento. Cada trabajador recibe un correo con su enlace único.";

  const ctaLabel = modo === "automatica" ? `Programar ${pendientes.length} envíos` : "Enviar ahora";
  // Mismo glifo que su tarjeta en `MODOS` (ya detectado ahí por
  // `check-icon-subset.mjs` vía `icono:`) — se computa acá aparte para que
  // el script no confunda la condición `automatica` del ternario con un
  // nombre de ícono cuando queda inline dentro de `name={...}`.
  // `event_upcoming` y no `event_repeat`: el ícono habla de lo que hace el
  // botón (dejar envíos agendados hacia adelante), no de la cadencia — ese es
  // el dato que ya describe la línea "Cadencia" del resumen. Mismo criterio
  // que el prototipo.
  const ctaIcono = modo === "automatica" ? "event_upcoming" : "send";
  // El caso "sin pendientes" ya no llega acá (el pie no dibuja el CTA en sólo
  // lectura); queda el de la empresa sin ningún programa candidato.
  const ctaDeshabilitado = !programa || enviando;

  async function confirmar() {
    if (!programa) return;
    if (nDestinatarios === 0) {
      toast.info("Elige al menos un destinatario.");
      return;
    }
    if (modo === "automatica" && !primerEnvio) {
      toast.info("Elige la fecha del primer envío.");
      return;
    }
    setEnviando(true);
    try {
      const resultado = await capacitacionesIaApi.programarEnvios(programa.id, {
        modo,
        cadencia: modo === "automatica" ? cadencia : undefined,
        primer_envio: modo === "automatica" ? primerEnvio : undefined,
        hora_envio: modo === "automatica" ? hora : undefined,
        destinatario_tipo: destinatarioTipo,
        destinatario_refs: destinatarioTipo === "empresa" ? [] : destinatarioRefs,
      });
      if (modo === "automatica") {
        toast.success(
          `${resultado.tematicas_afectadas} envíos programados para ${nDestinatarios} trabajadores, uno por ${CADENCIA_LABEL[cadencia]}.`
        );
      } else {
        const aviso =
          resultado.fallidas > 0
            ? ` ${resultado.fallidas} no se pudieron enviar — puedes reintentar desde Capacitaciones.`
            : "";
        toast.success(`Capacitación enviada a ${nDestinatarios} trabajadores.${aviso}`);
      }
      onProgramado(programa.id, new Date(`${modo === "automatica" ? primerEnvio : hoySantiago()}T00:00:00`));
      onCerrar();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.message ? err.message : "No se pudo programar el envío."
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Sheet open={abierto} onOpenChange={(v) => !v && !enviando && onCerrar()}>
      <SheetContent className="sm:w-[560px] sm:max-w-[94vw]">
        <SheetHeader>
          <SheetTitle>{soloLectura ? "Programación de envíos" : "Programar envíos"}</SheetTitle>
          <SheetDescription>
            {soloLectura
              ? "Así quedó configurado este programa."
              : "Define cuándo sale y a quién le llega."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-[18px] overflow-y-auto p-4">
          <div>
            <Label htmlFor="envio-programa" className="mb-1.5 text-[12.5px]">
              Programa
            </Label>
            <Select value={programaId !== null ? String(programaId) : ""} onValueChange={cambiarPrograma}>
              <SelectTrigger id="envio-programa" className="w-full">
                <SelectValue placeholder="Elige un programa" />
              </SelectTrigger>
              <SelectContent>
                {candidatos.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.nombre ?? "Programa sin nombre"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {programa && (
              <div className="bg-surface-muted border-border-subtle mt-2 flex items-center gap-1.5 rounded-lg border px-[11px] py-2">
                <Icon name="playlist_add_check" size={16} className="text-icon-soft shrink-0" />
                <span className="text-[11.5px] leading-[1.45] text-muted-foreground">{resumenTem}</span>
              </div>
            )}
          </div>

          {/* Con el manual oculto no hay modo que elegir: el bloque entero
              desaparece en vez de quedar como una tarjeta única siempre
              marcada (ver `ENVIO_MANUAL_HABILITADO`). Los campos de cadencia,
              fecha y hora de abajo siguen apareciendo igual: son la
              programación, no el modo. */}
          {muestraCalendario && ENVIO_MANUAL_HABILITADO && (
            <div>
              <Label className="mb-1.5 text-[12.5px]">Modo de envío</Label>
              <TarjetasOpcion<ModoEnvio>
                opciones={MODOS}
                valor={modo}
                onCambiar={setModo}
                columnas={2}
                disabled={soloLectura}
              />
            </div>
          )}

          {muestraCalendario && modo === "automatica" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="envio-cadencia" className="mb-1.5 text-[12.5px]">
                  Cadencia
                </Label>
                <Select
                  value={cadencia}
                  onValueChange={(v) => setCadencia(v as EnvioCadencia)}
                  disabled={soloLectura}
                >
                  <SelectTrigger id="envio-cadencia" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="semanal">Una temática por semana</SelectItem>
                    <SelectItem value="quincenal">Una temática cada 15 días</SelectItem>
                    <SelectItem value="mensual">Una temática por mes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="envio-primer" className="mb-1.5 text-[12.5px]">
                  {soloLectura ? "Primer envío programado" : "Primer envío"}
                </Label>
                {/* Un solo camino para los dos modos: el formato dd/mm/aaaa lo
                    fija el componente, así que ya no hay una rama de sólo
                    lectura con su propio formateo. El nativo se fue por
                    completo — ver el docblock de `CampoFecha`. */}
                <CampoFecha
                  id="envio-primer"
                  value={primerEnvio}
                  onChange={setPrimerEnvio}
                  disabled={soloLectura}
                  aria-label="Fecha del primer envío"
                />
              </div>
              <div>
                <Label htmlFor="envio-hora" className="mb-1.5 text-[12.5px]">
                  Hora
                </Label>
                <CampoHora
                  id="envio-hora"
                  value={hora}
                  onChange={setHora}
                  disabled={soloLectura}
                />
              </div>
            </div>
          )}

          {/* Separa el bloque de calendario de los destinatarios. Sin calendario
              arriba quedaría una línea suelta bajo el encabezado. */}
          {muestraCalendario && <div className="border-border-subtle border-t" />}

          <div>
            <Label className="mb-0.5 text-[12.5px]">Destinatarios</Label>
            <p className="text-soft mb-2.5 text-[11.5px]">
              {soloLectura
                ? "El alcance con el que quedaron estos envíos."
                : "Elige el alcance. El contador se actualiza en vivo."}
            </p>
            <TarjetasOpcion<TematicaDestinatarioTipo>
              opciones={tarjetasDestino}
              valor={destinatarioTipo}
              onCambiar={elegirTipo}
              columnas={2}
              disabled={soloLectura}
            />
          </div>

          {/* Sólo al programar: el aviso habla de quién NO va a recibir, y en la
              ficha de lo ya programado no hay decisión que corregir con eso. */}
          {!soloLectura && destinatarioTipo === "empresa" && excluidos && hayExcluidos && (
            <div className="border-warning/25 bg-warning-bg rounded-lg border p-3">
              <div className="flex items-start gap-2">
                <Icon name="warning" size={16} className="text-warning mt-px shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-warning text-[12px] leading-[1.5] font-medium">
                    {excluidos.total === 1
                      ? "1 trabajador no recibirá esta capacitación"
                      : `${excluidos.total} trabajadores no recibirán esta capacitación`}
                  </p>
                  <p className="text-[11.5px] leading-[1.5] text-muted-foreground">
                    Solo se envía a trabajadores activos y con correo registrado.
                  </p>
                  <button
                    type="button"
                    onClick={() => setVerExcluidos((v) => !v)}
                    aria-expanded={verExcluidos}
                    className="text-warning mt-1.5 flex items-center gap-0.5 text-[11.5px] font-medium hover:underline"
                  >
                    <Icon name={verExcluidos ? "expand_less" : "expand_more"} size={15} />
                    {verExcluidos ? "Ocultar detalle" : "Ver detalle"}
                  </button>

                  {verExcluidos && (
                    <ul className="border-warning/20 mt-2 flex flex-col gap-1 border-t pt-2">
                      {excluidos.personas.map((p) => (
                        <li
                          key={p.uuid}
                          className="flex items-baseline justify-between gap-2 text-[11.5px] leading-[1.5]"
                        >
                          <span className="truncate text-muted-foreground">{p.nombre}</span>
                          {/* Rótulo de ESTADO, no adjetivo: "Inactivo" es el
                              mismo término que ya usa el listado de usuarios,
                              y así no hay que concordar género con la persona. */}
                          <span className="text-soft shrink-0">
                            {p.motivo === "inactivo" ? "Inactivo" : "Sin correo"}
                          </span>
                        </li>
                      ))}
                      {excluidos.total > excluidos.personas.length && (
                        // El backend capa la lista en 25 pero `total` es exacto,
                        // así que el resto se cuenta contra `total`.
                        <li className="text-soft pt-0.5 text-[11.5px]">
                          y {excluidos.total - excluidos.personas.length} más
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {destinatarioTipo === "cargo" && opciones && opciones.cargos.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[12.5px] font-medium">Cargos</span>
                <span className="fc-num text-soft text-[11px]">
                  {destinatarioRefs.length} seleccionados
                </span>
              </div>
              <ChipsSeleccion
                opciones={opciones.cargos.map((c) => ({ id: c.id, etiqueta: c.nombre, conteo: c.conteo }))}
                seleccionados={destinatarioRefs as number[]}
                onCambiar={(ids) => setDestinatarioRefs(ids)}
                disabled={soloLectura}
                aria-label="Cargos destinatarios"
              />
            </div>
          )}

          {destinatarioTipo === "area" && opciones && opciones.areas.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[12.5px] font-medium">Áreas</span>
                <span className="fc-num text-soft text-[11px]">
                  {destinatarioRefs.length} seleccionadas
                </span>
              </div>
              <ChipsSeleccion
                opciones={opciones.areas.map((a) => ({ id: a.id, etiqueta: a.nombre, conteo: a.conteo }))}
                seleccionados={destinatarioRefs as number[]}
                onCambiar={(ids) => setDestinatarioRefs(ids)}
                disabled={soloLectura}
                aria-label="Áreas destinatarias"
              />
            </div>
          )}

          {destinatarioTipo === "persona" && (
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[12.5px] font-medium">Trabajadores</span>
                <span className="fc-num text-soft text-[11px]">
                  {destinatarioRefs.length} seleccionados
                </span>
              </div>
              <MultiSelect
                options={personas.map((p) => ({
                  value: p.uuid,
                  label: `${p.nombre} · ${formatRut(p.rut)}`,
                  // El RUT se muestra con puntos, pero se busca igual escribiéndolo
                  // corrido: el label formateado no calza contra "152111924".
                  keywords: [cleanRut(p.rut)],
                }))}
                value={destinatarioRefs as string[]}
                onChange={(vals) => setDestinatarioRefs(vals)}
                placeholder={cargandoPersonas ? "Cargando…" : "Busca por nombre o RUT"}
                searchPlaceholder="Nombre o RUT"
                disabled={cargandoPersonas || soloLectura}
              />
              {!soloLectura && hayExcluidos && !cargandoPersonas && (
                // La lista ya no ofrece inactivos ni gente sin correo. Decirlo
                // acá evita la otra cara del mismo problema: buscar a alguien,
                // no encontrarlo y no saber por qué.
                <p className="text-soft mt-1.5 flex items-start gap-1 text-[11.5px] leading-[1.5]">
                  <Icon name="info" size={14} className="mt-px shrink-0" />
                  <span>
                    {excluidos!.total === 1
                      ? "1 trabajador no aparece en esta lista"
                      : `${excluidos!.total} trabajadores no aparecen en esta lista`}
                    : solo se puede enviar a quienes están activos y tienen correo registrado.
                  </span>
                </p>
              )}
            </div>
          )}

          <div className="bg-primary-50 border-primary-200 flex flex-col gap-2.5 rounded-lg border p-3.5">
            <div className="flex items-center gap-2.5">
              <Icon name="groups" size={19} className="text-primary-hover shrink-0" />
              <div className="flex-1">
                <div className="text-[12px] text-muted-foreground">Llega a</div>
                <div className="fc-num text-[18px] font-semibold">{nDestinatarios} trabajadores</div>
              </div>
              <div className="text-right">
                <div className="text-[12px] text-muted-foreground">
                  {!soloLectura
                    ? "Envíos generados"
                    : programacion !== null
                      ? "Envíos programados"
                      : "Envíos realizados"}
                </div>
                <div className="fc-num text-[18px] font-semibold">{enviosDeLaTanda}</div>
              </div>
            </div>
            <div className="border-primary-200 border-t" />
            <p className="text-[11.5px] leading-[1.5] text-muted-foreground">{resumenEnvio}</p>
          </div>
        </div>

        <SheetFooter>
          <span className="text-soft flex-1 text-xs">
            {!programa
              ? "Elige un programa."
              : soloLectura
                ? `${nDestinatarios} destinatarios · ${enviosDeLaTanda} envíos ${programacion !== null ? "programados" : "realizados"}`
                : `${nDestinatarios} destinatarios · ${pendientes.length} envíos`}
          </span>
          {soloLectura ? (
            // Un solo botón: no hay nada que cancelar ni que confirmar. Dejar
            // el CTA deshabilitado ("Nada por programar") invitaba a buscar
            // qué desbloquearlo — que es justo lo que no existe.
            <Button size="sm" onClick={onCerrar}>
              Cerrar
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={onCerrar} disabled={enviando}>
                Cancelar
              </Button>
              <Button size="sm" onClick={confirmar} disabled={ctaDeshabilitado}>
                <Icon name={ctaIcono} size={16} />
                {enviando ? "Enviando…" : ctaLabel}
              </Button>
            </>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
